import { useTranslation } from "@redotech/redo-i18n";
import { SubjectLineMatchType } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { SubjectLineState } from "@redotech/redo-return-ui/flows/rules/conditions/subject-line";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { memo, useState } from "react";
import { commitPendingTagInput } from "../commit-pending-tag-input";

export const SubjectLineDetails = memo(function SubjectLineDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<SubjectLineState>) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");

  const matchModeOptions = [
    {
      id: SubjectLineMatchType.EXACT,
      type: "text" as const,
      text: t("entire subject line is exactly"),
      value: SubjectLineMatchType.EXACT,
    },
    {
      id: SubjectLineMatchType.CONTAINS,
      type: "text" as const,
      text: t("contains"),
      value: SubjectLineMatchType.CONTAINS,
    },
  ];

  const selectedMatchMode = matchModeOptions.find(
    (option) => option.id === state.matchMode,
  );

  const commitPendingInput = () => {
    if (disabled) {
      return;
    }
    const queryStrings = commitPendingTagInput(
      state.queryStrings || [],
      inputValue,
    );
    if (!queryStrings) {
      return;
    }
    setState({ ...state, queryStrings });
    setInputValue("");
  };

  return (
    <>
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        label={t("Match mode")}
        options={matchModeOptions}
        optionSelected={({ item }) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          setState({ ...state, matchMode: item.id as typeof state.matchMode });
        }}
        selectedItem={selectedMatchMode}
      />
      <RedoTagInputField
        delimiters={["newline"]}
        inputValue={inputValue}
        label={t("Subject text to match")}
        onBlur={commitPendingInput}
        placeholder={t("Enter subject text to match")}
        setInputValue={setInputValue}
        setTags={(queryStrings) => {
          const uniqueStrings = Array.from(new Set(queryStrings));
          setState({ ...state, queryStrings: uniqueStrings });
        }}
        state={disabled ? "disabled" : "default"}
        tags={(state.queryStrings || []).map((text) => ({ text }))}
      />

      <Flex>
        <LabeledInput label={t("Case sensitive")}>
          <RedoCheckbox
            setValue={(value) => {
              if (typeof value === "boolean") {
                setState({ ...state, caseSensitive: value });
              }
            }}
            value={state.caseSensitive}
          />
        </LabeledInput>{" "}
      </Flex>
    </>
  );
});
